import { useEffect, useMemo, useRef, useState } from 'react';
import { usePinToLatest } from './pinToLatest';
import { onListKeyDown } from './reply';
import { ChevronLeft, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import { CatchUpModal, useCanCatchUp } from '@/features/ai/CatchUp';
import { CHANNEL_CANNOT_REPLY, type Message } from '@tradeoutlet/shared';
import { errorMessage } from '@/lib/api';
import { messageStore, useConversationState } from '@/lib/messageStore';
import { CloseLink, ErrorBox, Spinner } from '@/components/ui';
import { TypingLine } from './TypingLine';
import { Composer } from './Composer';
import { MessageItem, PendingItem, type AckContext } from './MessageItem';
import { showThreadSummary } from './threadHeader';
import { runPositions, type BubbleRun } from './bubbles';

/** Beside the conversation on desktop; on the phone (full) it is its own screen with a back button (mobile Phase 1). */
export function ThreadPanel({ conversationId, rootId, memberIds, onClose, ack, full, subtitle, bubbles, canReply = true, canSchedule }: { conversationId: string; rootId: string; memberIds?: string[]; onClose: () => void; ack?: AckContext; full?: boolean; subtitle?: string;
  /** t520u: false when the channel's permissions don't let this person reply (the server refuses it too). */
  canReply?: boolean;
  /** t622u: offer Schedule next to Send. */
  canSchedule?: boolean;
  /** t278u: bubble layout; the value says whether names and avatars show (false in a 1:1 DM). Undefined: the comment layout. */
  bubbles?: boolean }) {
  const state = useConversationState(conversationId);
  const [root, setRoot] = useState<Message | null>(null);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  // t236u: the phone's full-screen thread keeps the latest reply above the composer like the conversation does.
  const listRef = useRef<HTMLDivElement>(null);
  const composerBox = useRef<HTMLDivElement>(null);
  usePinToLatest(listRef, composerBox, { enabled: Boolean(full) }, [rootId]);
  const [summary, setSummary] = useState(false);
  const canCatchUp = useCanCatchUp();
  const replies = state.threads[rootId];
  const liveRoot = state.messages.find(m => m.id === rootId) ?? root;
  const runs = useMemo(() => {
    if (bubbles === undefined) return null;
    const out = new Map<string, BubbleRun>([['root', { first: true, last: true, names: bubbles }]]);
    for (const [id, r] of runPositions(replies ?? [])) out.set(id, { ...r, names: bubbles });
    return out;
  }, [replies, bubbles]);
  const bubbleOf = (id: string) => runs?.get(id);

  useEffect(() => {
    setRoot(null);
    setError(null);
    messageStore.loadThread(conversationId, rootId).then(setRoot).catch(e => setError(errorMessage(e)));
  }, [conversationId, rootId]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [replies?.length, state.pending.length]);

  return (
    <aside className={clsx('flex flex-col bg-white dark:bg-slate-950', full ? 'min-w-0 flex-1' : 'w-[26rem] shrink-0 border-l border-slate-200 dark:border-slate-800')} data-testid="thread-panel">
      <header className={clsx('flex h-14 shrink-0 items-center justify-between border-b border-slate-200 dark:border-slate-800', full ? 'gap-1 pl-1 pr-2 pt-[env(safe-area-inset-top)] box-content' : 'px-4')}>
        {full && <button type="button" onClick={onClose} data-testid="thread-back" aria-label="Back to conversation"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-700 active:bg-slate-100 dark:text-slate-200 dark:active:bg-slate-800"><ChevronLeft className="h-6 w-6" /></button>}
        <div className="min-w-0">
          <div className="font-semibold">Thread</div>
          {full && subtitle && <div className="truncate text-xs text-slate-500">{subtitle}</div>}
        </div>
        <div className="ml-auto mr-1">{canCatchUp && (replies?.length ?? 0) >= 3 && <button onClick={() => setSummary(true)} className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"><Sparkles className="h-3.5 w-3.5" />Summarise</button>}</div>
        {!full && <CloseLink label="Close thread" onClose={onClose} />}
      </header>
      <div ref={listRef} className="scroll-thin min-h-0 flex-1 overflow-y-auto py-2 outline-none" data-message-list="thread" tabIndex={0} aria-label="Thread messages" onKeyDown={onListKeyDown}>
        <ErrorBox error={error} />
        {!liveRoot && !error && <div className="flex justify-center p-6"><Spinner /></div>}
        {liveRoot && <MessageItem m={liveRoot} compact={false} inThread ack={ack} bubble={bubbleOf('root')} />}
        {liveRoot && (
          <div className="my-2 flex items-center gap-2 px-5 text-xs text-slate-500" data-testid="thread-divider">
            {/* t267u: the phone's thread screen shows every reply, so the count only repeats what is visible. */}
            {(!full || !replies || showThreadSummary({ collapsed: false, shownCount: replies.length, replyCount: liveRoot.reply_count })) &&
              <span data-testid="thread-divider-count">{replies?.length ?? liveRoot.reply_count} {(replies?.length ?? liveRoot.reply_count) === 1 ? 'reply' : 'replies'}</span>}
            <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
          </div>
        )}
        {(replies ?? []).map((m, i, arr) => (
          <MessageItem key={m.id} m={m} inThread ack={ack} bubble={bubbleOf(m.id)}
            compact={i > 0 && arr[i - 1].sender_id === m.sender_id && new Date(m.created_at).getTime() - new Date(arr[i - 1].created_at).getTime() < 5 * 60_000} />
        ))}
        {state.pending.filter(p => p.parent_id === rootId).map(p => <PendingItem key={p.client_message_id} p={p} conversationId={conversationId} bubble={bubbles !== undefined} />)}
        <div ref={endRef} />
      </div>
      {summary && <CatchUpModal conversationId={conversationId} parentId={rootId} title="Thread" onClose={() => setSummary(false)} onJump={id => document.getElementById(`m-${id}`)?.scrollIntoView({ block: 'center' })} />}
      <div ref={composerBox} className={clsx(full && 'safe-b')}>{canReply
        ? <><TypingLine conversationId={conversationId} parentId={rootId} className="px-5" /><Composer key={rootId} conversationId={conversationId} parentId={rootId} placeholder="Reply…" memberIds={memberIds} autoFocus={!full} canSchedule={canSchedule} /></>
        : <p className="px-5 py-4 text-center text-sm text-slate-500" data-testid="composer-cannot-reply">{CHANNEL_CANNOT_REPLY}</p>}</div>
    </aside>
  );
}
